import React from 'react';
import { Control } from 'react-hook-form'
import { FieldsType, FieldRule, getByChildFormFieldRule } from '../../../containers/common/projectMst'
import { Inputs } from "../../../containers/facilityLotteryReservationForm/facilityLotteryReservationFormService"
import { GetProjectDetailDto } from "../../../dataAccess/webApi/dto/projectsDto"
import { PostDecodeMstDto } from '../../../dataAccess/webApi/dto/decodeMstDto';
import { GContainer, GItem } from "../common/grids"
import { InfoCard } from "../common/infoCard"
import { TitleLabel } from "../common/titleLabel"
import ChildContent from "../common/reservationDetail/childContent"
import DiagonosisField, { DiagonosisFieldProps } from '../facilityLotteryReservationForm/fields/diagonosisField';
import SymptomField, { SymptomFieldProps } from '../facilityLotteryReservationForm/fields/symptomField';
import EpidemicField, { EpidemicFieldProps } from '../facilityLotteryReservationForm/fields/epidemicField';
import MedicineField, { MedicineFieldProps } from '../facilityLotteryReservationForm/fields/medicineField';
import BodyTemperatureField, { BodyTempatureFieldProps } from '../facilityLotteryReservationForm/fields/bodyTempatureField';
import FebrileSeizuresField, { FebrileSeizuresFieldProps } from '../facilityLotteryReservationForm/fields/febrileSeizuresField';
import MedicalDocField, { MedicalDocFieldProps } from '../facilityLotteryReservationForm/fields/medicalDocField';
import EmergencyActionField, { EmergencyActionFieldProps } from '../facilityLotteryReservationForm/fields/emergencyActionField';
import HeightField, { HeightFieldProps } from '../facilityLotteryReservationForm/fields/heightField';
import WeightField, { WeightFieldProps } from '../facilityLotteryReservationForm/fields/weightField';
import EatingMethodField, { EatingMethodFieldProps } from '../facilityLotteryReservationForm/fields/eatingMethodField';
import FoodAllergyField, { FoodAllergyFieldProps } from '../facilityLotteryReservationForm/fields/foodAllergyField';
import EatingAbilityField, { EatingAbilityFieldProps } from '../facilityLotteryReservationForm/fields/eatingAbilityField';
import ActionsField, { ActionsFieldProps } from '../facilityLotteryReservationForm/fields/actionsField';
import ToiletAbilityField, { ToiletAbilityFieldProps } from '../facilityLotteryReservationForm/fields/toiletAbilityField';
import ConsiderationsField, { ConsiderationsFieldProps } from "./fields/considerationsField";
import ReductionTypeField, { ReductionTypeFieldProps } from '../facilityLotteryReservationForm/fields/reductionTypeField';
import AttachedDocField, { AttachedDocFieldProps } from '../facilityLotteryReservationForm/fields/attachedDocField';
import SiblingsLotteryResultReferenceFlagField from '../facilityLotteryReservationForm/fields/siblingsLotteryResultReferenceFlagField';
import { ChildLotteryReservationInfoType } from '../../../containers/common/lotteryReservation';
import { Flag, reservationFieldUsage } from '../../../containers/common/constant/classification';
import FacilityinputField, { FacilityInputFieldProps } from './fields/facilityInputsField';
import { facilityInputFields } from '../../../dataAccess/webApi/dto/facilitiesDto';

type Field<T> = {
    component:React.FC<T>
    getProps: (rule: FieldRule, childNum: number) => T
}

type SortedField<T> = Field<T> & {
    rule: FieldRule
}

type ByChildFormUnitProps = {
    control: Control<Inputs>
    projectMst: GetProjectDetailDto
    childInfoList: ChildLotteryReservationInfoType[]
    diagnosisOptions: PostDecodeMstDto[]
    symptomOptions: PostDecodeMstDto[]
    epidemicOptions: PostDecodeMstDto[]
    emergencyOptions: PostDecodeMstDto[]
    eatingMethodOptions: PostDecodeMstDto[]
    brestFeedingDetailOptions: PostDecodeMstDto[]
    weaningDetailOptions: PostDecodeMstDto[]
    eatingAbilityOptions: PostDecodeMstDto[]
    actionsOptions: PostDecodeMstDto[]
    toiletAbilityOptions: PostDecodeMstDto[]
    reductionOptions: PostDecodeMstDto[]
    showSiblingsLotteryResultReferenceFlag: boolean
    facilityInputFieldsDisplayFlag: string
    facilityInputFields?: facilityInputFields[]
}

interface FieldComponents {
    diagonosis: Field<DiagonosisFieldProps>;
    symptom: Field<SymptomFieldProps>;
    epidemic: Field<EpidemicFieldProps>;
    medicine: Field<MedicineFieldProps>;
    bodyTemp: Field<BodyTempatureFieldProps>;
    febrileSeizures: Field<FebrileSeizuresFieldProps>;
    medicalDoc: Field<MedicalDocFieldProps>;
    emergency: Field<EmergencyActionFieldProps>;
    height: Field<HeightFieldProps>;
    weight: Field<WeightFieldProps>;
    eatingMethod: Field<EatingMethodFieldProps>;
    foodAllergy: Field<FoodAllergyFieldProps>;
    eatingAbility: Field<EatingAbilityFieldProps>;
    action: Field<ActionsFieldProps>;
    toiletAbility: Field<ToiletAbilityFieldProps>;
    considerations: Field<ConsiderationsFieldProps>;
    reduction: Field<ReductionTypeFieldProps>;
    attachedDoc: Field<AttachedDocFieldProps>;
    facilityInputs?: Field<FacilityInputFieldProps>
}

const ByChildFormUnitForLottery: React.FC<ByChildFormUnitProps> = (props) => {
    const { 
        control, 
        projectMst, 
        childInfoList,
        diagnosisOptions,
        symptomOptions,
        epidemicOptions,
        emergencyOptions,
        eatingMethodOptions,
        brestFeedingDetailOptions,
        weaningDetailOptions,
        eatingAbilityOptions,
        actionsOptions,
        toiletAbilityOptions,
        reductionOptions,
        showSiblingsLotteryResultReferenceFlag,
        facilityInputFieldsDisplayFlag,
        facilityInputFields
    } = props
    
    const fieldComponents: FieldComponents = {
        diagonosis:  {
            component: DiagonosisField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: diagnosisOptions,
                control
            })
        },
        symptom: {
            component: SymptomField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: symptomOptions,
                control
            })
        },
        epidemic: {
            component: EpidemicField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: epidemicOptions,
                control
            })
        },
        medicine: {
            component: MedicineField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required
            })
        },
        bodyTemp: {
            component: BodyTemperatureField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                control
            })
        },
        febrileSeizures: {
            component: FebrileSeizuresField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required
            })
        },
        medicalDoc: {
            component: MedicalDocField,
            getProps: (rule, childNum) => ({
                childNum,
                note: projectMst.medicalDocNote,
                medicalDocUsage: projectMst.medicalDocUsage,
                control
            })
        },
        emergency: {
            component: EmergencyActionField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: emergencyOptions,
                control
            })
        },
        height: {
            component: HeightField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                control
            })
        },
        weight: {
            component: WeightField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                control
            })
        },
        eatingMethod: {
            component: EatingMethodField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                methodOptions: eatingMethodOptions,
                brestFeddingDetailOptions: brestFeedingDetailOptions,
                weaningDetailOptions: weaningDetailOptions,
                control
            })
        },
        foodAllergy: {
            component: FoodAllergyField,
            getProps: (rule, childNum) => ({
                childNum: childNum,
                required: rule.required
            })
        },
        eatingAbility: {
            component: EatingAbilityField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: eatingAbilityOptions,
                control
            })
        },
        action: {
            component: ActionsField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: actionsOptions,
                control
            })
        },
        toiletAbility: {
            component: ToiletAbilityField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                options: toiletAbilityOptions,
                control
            })
        },
        considerations: {
            component: ConsiderationsField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
            })
        },
        reduction: {
            component: ReductionTypeField,
            getProps: (rule, childNum) => ({
                childNum,
                required: rule.required,
                useConsent: projectMst.reductionConsentInput !== reservationFieldUsage.noUse,
                options: reductionOptions,
                note: projectMst.reductionNote,
                consentNote: projectMst.reductionConsentNote,
                control
            })
        },
        attachedDoc: {
            component: AttachedDocField,
            getProps: (_rule, childNum) => ({
                childNum,
                attachedDocInput: projectMst.attachedDocInput,
                control
            })
        },
    }
    const fieldRule = getByChildFormFieldRule(projectMst)
    const isMultipleChild = childInfoList.length !== 1
    
    const sortedFields: SortedField<any>[] = Object.keys(fieldRule)
        .filter((fieldKey) => {
            return fieldRule[fieldKey as keyof FieldsType<FieldRule>].show
        }) 
        .map((fieldKey) => {
            const field = fieldKey as keyof FieldsType<FieldRule>;
            return {
                component: fieldComponents[field].component,
                getProps: fieldComponents[field].getProps,
                rule: fieldRule[field],
            };
        })
        .sort((a, b) => (a.rule.order ?? Number.MAX_SAFE_INTEGER) - (b.rule.order ?? Number.MAX_SAFE_INTEGER));
    
        if (facilityInputFieldsDisplayFlag === Flag.ON && facilityInputFields ) {
            const facilityFields: SortedField<any>[] = facilityInputFields.map((input) => ({
            component: FacilityinputField,
            getProps: (rule: FieldRule, childNum: number) => ({
                childNum,
                ...input
            }),
            rule: {
                show: true,
                required: true,
                order: Number(input.order)
            }
        }))
            .sort((a, b) => a.rule.order - b.rule.order);

        sortedFields.push(...facilityFields)
    }

    return (
        <>
            {childInfoList.map((childInfo, childNum) => {
            
                return (
                    <React.Fragment key={childNum}>
                        {
                            ( isMultipleChild && sortedFields.length > 0 ) && (
                                <>
                                    <GItem xs={12}>
                                        <TitleLabel>お子さま{childNum + 1}</TitleLabel>
                                    </GItem>
                                    <InfoCard>
                                        <GContainer>
                                            <ChildContent childInfo={childInfo} />
                                        </GContainer>
                                    </InfoCard>
                                </>
                            )
                        }
                        <>
                            {
                                sortedFields.map(({component: Component, getProps, rule}, index) => (
                                    <Component key={index} {...getProps(rule, childNum)} />
                                ))
                            }
                            {
                                showSiblingsLotteryResultReferenceFlag && childInfo.childInfo?.brothersSistersFlag === Flag.ON && (
                                    <SiblingsLotteryResultReferenceFlagField childNum={childNum} required/>
                                )
                            }
                        </>
                    </React.Fragment>
                
                )
            })}
            
        </>
    )
}



export default ByChildFormUnitForLottery;
